import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
// import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { loginAtom } from '../../../lib/auth';

import BrunchDiningIcon from '@mui/icons-material/BrunchDining';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HelpIcon from '@mui/icons-material/Help';

import {
  Alert,
  Avatar,
  Box,
  Button as Btn,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Slider,
  Tab,
  Tabs,
  Tooltip,
  useMediaQuery,
} from '@mui/material';

import {
  Button,
  FileUpload,
  Select,
  TextInput,
  TimePickerNew,
  Typography
} from '../../../components/v2/styled';
import {
  FaceUnhappy,
  FaceHappy,
  FaceVeryUnhappy,
  FaceVeryHappy
} from '../../../components/v2/faces';
import { Base } from './Base';
import { Removal } from './Removal';

import HelpersLanguage from '../../../actions/helpers/language';

import { DiaryDay } from '../../../models/DiaryDay';
import { EatMoment as EatMmnt, EatMoment } from '../../../models/EatMoment';
import { FoodEntry } from '../../../models/FoodEntry';
import Add from '@mui/icons-material/Add';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Foodstuff } from '../../../models/Foodstuff';
import { fetch_all, fetch_one } from '../../../lib/v31lib';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import { FEATURES } from '../../../features';

const { DateTime } = require("luxon");

/**
 * Avatar for a single food entry. `get_item` is a backend URL; `item` is a locally
 * selected File. We memoize the object URL (and revoke it on change/unmount) so the
 * browser doesn't reload the image on every render — that reload was the source of the
 * avatar flicker.
 */
const FoodEntryAvatar: FunctionComponent<{
  entry: FoodEntry;
}> = ({entry}) => {
  const src = useMemo(
    () => !!entry.get_item ? entry.get_item : !!entry.item ? URL.createObjectURL(entry.item) : undefined,
    [entry.get_item, entry.item]
  );

  useEffect(() => {
    // Only revoke blob URLs we created ourselves; get_item is a backend URL we don't own.
    if (!!src && !!entry.item) {
      return () => { URL.revokeObjectURL(src); };
    }
  }, [src, entry.item]);

  return <Avatar src={src}><BrunchDiningIcon /></Avatar>;
};

type Props = {
  onClick: () => void;
  onDelete: () => void;
  open: boolean;
  setOpen: (b:boolean) => void;
  newEatMoment: EatMmnt;
  setNewEatMoment: (n:EatMmnt) => void;
  diaryDay?: DiaryDay;
  canDoCalculated?: boolean;
};

export const ClientEatMomentEdit: FunctionComponent<Props> = ({open, setOpen, onClick, onDelete, diaryDay, newEatMoment, setNewEatMoment, canDoCalculated: canDoCalculatedProp}) => {
  const theme = useTheme();
  // const navigate = useNavigate();
  const [login, setLogin] = useAtom(loginAtom);
  const {t, i18n} = useTranslation(['translations']);

  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [currentTab, setCurrentTab] = useState<string>((Object.values(login?.full_eat_groups || {}).filter(fff => fff.practices.length > 0).length > 0 ? 'eat_diary.groups.short' : 'eat_diary.groups.everything'));
  const [errors, setErrors] = useState<{[z:string]: string}>({});
  const [editing, setEditing] = useState<number>();
  const [removalOpen, setRemovalOpen] = useState<boolean>(false);
  const [originalEatMmnt, setOriginalEatMmnt] = useState<EatMmnt>();
  const [favLoaded, setFavLoaded] = useState<boolean>(false);
  const [newFoodEntry, setNewFoodEntry] = useState<FoodEntry>({entry: '', comment: '', foodstuff_portion: undefined});
// console.log(login?.full_eat_groups);
// console.log(DateTime.now().toFormat("t"));
  const [addingFoodLine, setAddingFoodLine] = useState<boolean>(false);
  const [addingFoodLinePerGram, setAddingFoodLinePerGram] = useState<boolean>(true);
  const [searchFood, setSearchFood] = useState<string>('');
  const [foodPortion, setFoodPortion] = useState<string>('');

  const canDoCalculated = FEATURES.CALCULATED_DIARY && canDoCalculatedProp !== false;

  const checkFieldValidity = (fieldName:string) => {
    if (currentTab === "eat_diary.groups.everything") return true;

    return Object.values(login?.full_eat_groups || {}).filter(ff => ff.practices.length > 0 && ff.profile.questions.indexOf(fieldName) > -1).length > 0
  }
  const checkHelperIcon = (fieldName:string) => {
    if (currentTab === "eat_diary.groups.everything") return '';

    let practices:string[] = [];
    Object.values(login?.full_eat_groups || {}).map((ff, i) => {
      if (ff.profile.questions.indexOf(fieldName) > -1) practices = [...practices, ...(ff.practices.map(qq => qq[1]))];
    });

    let str = `${t("eat_diary.groups.asked_by")}<ul>`;
    let pr = practices.map((i) => {
      str += `<li>${i}</li>`
    });
    str += `</ul>`;
    return <Typography html sx={{padding: 1}}>{str}</Typography>;
  }







  const {
    data: eatMomentFavourites,
    // isLoading: foodstuffsLoading,
    // isError: foodstuffsError,
    // isSuccess: foodstuffsSuccess,
    // refetch: clientsRefetch,
  } = useQuery({
    queryKey: ["diary_eat_moment_fav", login?.id],
    queryFn: () =>
      fetch_all<EatMoment>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'eat_moment',
          fields: [
            'id', 'type', 'food_entries_compiled', 'location', 'participants', 'activity', 'hunger', 'complaints', 'feeling',
            'given_entry', 'meal_name', 'meal_description', 'get_item', 'favourite_name'
          ],
          // sub_fields: {
          //   diary_food_entries: ['id', 'name']
          // },
          order: "updated_at DESC",
          filter: {
            search: '',
            advanced: {
              is_favourite: '1',
              user_id: login?.id,
              type: {
                breakfast: 'Diary::EatMoment::Breakfast',
                lunch: 'Diary::EatMoment::Lunch',
                diner: 'Diary::EatMoment::Diner',
                snack1: 'Diary::EatMoment::Snack1',
                snack2: 'Diary::EatMoment::Snack2',
                snack3: 'Diary::EatMoment::Snack3'
                }[newEatMoment.eat_type || '']
            }
          }
        },
        login
      ),
    enabled: !!login?.id && !!{
      breakfast: 'Diary::EatMoment::Breakfast',
      lunch: 'Diary::EatMoment::Lunch',
      diner: 'Diary::EatMoment::Diner',
      snack1: 'Diary::EatMoment::Snack1',
      snack2: 'Diary::EatMoment::Snack2',
      snack3: 'Diary::EatMoment::Snack3'
      }[newEatMoment.eat_type || '']
  });
  const {
    data: foodstuffs,
    isLoading: foodstuffsLoading,
    isError: foodstuffsError,
    isSuccess: foodstuffsSuccess,
    // refetch: clientsRefetch,
  } = useQuery({
    queryKey: ["foodstuffs", searchFood],
    queryFn: () =>
      fetch_all<Foodstuff>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'foodstuff',
          fields: ['id', 'full_name', 'foodstuff_measures'],
          sub_fields: {
            foodstuff_measures: ['id', 'full_name', 'grams']
          },
          order: "name ASC",
          filter: {
            search: searchFood,
            advanced: {
              // favourite: login?.id
              enabled: '1'
            }
          }
        },
        login
      ),
    enabled: !!searchFood
  });
  const {
    data: favFoodstuffs,
    isLoading: favFoodstuffsLoading,
    isError: favFoodstuffsError,
    isSuccess: favFoodstuffsSuccess,
    refetch: favFoodstuffsRefetch,
  } = useQuery({
    queryKey: ["foodstuffs", "favourite", login?.id],
    queryFn: () =>
      fetch_all<Foodstuff>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'foodstuff',
          fields: ['id', 'full_name', 'foodstuff_measures'],
          sub_fields: {
            foodstuff_measures: ['id', 'full_name', 'grams']
          },
          order: "name ASC",
          filter: {
            search: '',
            advanced: {
              favourite: login?.id
              // enabled: '1'
            }
          }
        },
        login
      ),
    enabled: !!login?.id
  });

  











  const mutationCustomAction = useMutation({
    mutationFn: (pars: {
      formData: any;
    }) => {
      return fetch_one<{
        custom_result: boolean
      }>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/custom_action`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      favFoodstuffsRefetch();
    },
  });





  






  const selectContentWithId = (field?:{[z:string]: string}, extraFallback?:string) => {
    let correct = (field || {})[HelpersLanguage.mapping_to_id(i18n.resolvedLanguage || 'nl')];
    if (!!correct) return correct;
    let fallback = (field || {})['nl'];
    if (!!fallback) return fallback;
    let anyFallback = Object.values(field || {}).filter(fff => !!fff)[0];
    if (!!anyFallback) return anyFallback;
    if (!!extraFallback) return extraFallback;
    return t("general.translation_fallbacks.no_translation_available")
  }

  useEffect(() => {
    setNewFoodEntry({entry: '', item: undefined, foodstuff_id: undefined, foodstuff_portion: undefined, foodstuff_measure_id: undefined});
    setSearchFood('');
    setFavLoaded(false);
    setOriginalEatMmnt(newEatMoment);
  }, [open]);

  useEffect(() => {
    setNewFoodEntry({
      ...newFoodEntry,
      foodstuff_portion: !!foodPortion ? parseFloat(foodPortion.replace(",", ".")) : undefined
    });
  }, [foodPortion]);



  return (<>
    <Base
      name="eat_moment_edit"
      title={`${t("client.dashboard.eat_diary.new_entry_title", "Nieuwe ingave")}${newEatMoment.eat_type ? ` (${t(`client.diary.${newEatMoment.eat_type}`)})` : ''} (${DateTime.fromISO(diaryDay?.day).toLocaleString({ weekday: 'long', month: 'long', day: '2-digit' }, {locale: (i18n.resolvedLanguage || 'nl')})})`}
      fullWidth
      warningOnClose={Object.entries(originalEatMmnt || {}).filter(fff => !!fff[1]).sort().toString()!==Object.entries(newEatMoment).filter(fff => !!fff[1]).sort().toString() }
      onClose={() => {
        setNewEatMoment({feeling: -1, hunger: -1, item: undefined});
        setSearchFood('');
        setNewFoodEntry({entry: '', item: undefined, foodstuff_id: undefined, foodstuff_portion: undefined, foodstuff_measure_id: undefined});
        setAddingFoodLine(false);
        setCurrentTab((Object.values(login?.full_eat_groups || {}).filter(fff => fff.practices.length > 0).length > 0 ? 'eat_diary.groups.short' : 'eat_diary.groups.everything'));
      }}
      fullScreen={fullScreen}
      maxWidth="lg"
      content={<>
      
        <Tabs
          value={currentTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          onChange={(e, value) => {setCurrentTab(value);}} indicatorColor="primary" textColor="primary" centered={false}>
          <Tab label={t("eat_diary.groups.everything")} value={"eat_diary.groups.everything"} />
          {Object.values(login?.full_eat_groups || {}).filter(fff => fff.practices.length > 0).length > 0 && <Tab label={t("eat_diary.groups.short")} value={"eat_diary.groups.short"} />}
          <Tab label={t("eat_diary.tab.food_detail")} value={"eat_diary.tab.food_detail"} />
          <Tab label={t("eat_diary.tab.food_lines")} value={"eat_diary.tab.food_lines"} />
        </Tabs>

        <Divider />

        {currentTab !== "eat_diary.tab.food_lines" && currentTab !== "eat_diary.tab.food_detail" && <Box>
          <Grid container sx={{
            marginTop: 1
          }} spacing={2}>
            {!!canDoCalculated && (eatMomentFavourites || []).length > 0 && !newEatMoment.is_favourite && !favLoaded && !newEatMoment.id && <Grid xs={3} item>
              <Select
                fullWidth
                caption={t("shared.nutri_mail.apply_favourite")}
                displayEmpty
                backend
                id="apply_favourite"
                value={''}
                setValue={(v) => {
                  console.log(v);
                  let fav = undefined;
                  fav = (eatMomentFavourites || []).filter(fff => fff.id === v)[0];

                  if (!!fav) {
                    setFavLoaded(true);
                    setNewEatMoment({
                      ...newEatMoment,
                      location: fav.location,
                      participants: fav.participants,
                      activity: fav.activity,
                      hunger: fav.hunger,
                      complaints: fav.complaints,
                      feeling: fav.feeling,
                      given_entry: fav.given_entry,
                      meal_name: fav.meal_name,
                      meal_description: fav.meal_description,
                      // item: fav.get_item,
                      food_entries: (fav.food_entries_compiled || []).map(mm => {
                        return {...mm, id: undefined};
                      })
                    });
                  }
                }}
              >
                {(eatMomentFavourites || [])?.map(mm => <MenuItem value={mm.id}>{mm.favourite_name}</MenuItem>)}
              </Select>
            </Grid>}
            <Grid item sm={3} xs={12} hidden={!checkFieldValidity('time')}>
              <Typography sx={{marginTop: 1, fontSize: '0.7rem'}}>{t("client.models.eat_moments.entry_time", "Tijdstip")}</Typography>
              {!!errors.entry_time && <React.Fragment>
                <Typography variant={"caption"} sx={{marginTop: 1, color: theme.palette.secondary.main}}>{t("client.models.eat_moments.errors.entry_time", "Dit is een verplicht veld")}</Typography>
              </React.Fragment>}

              <TimePickerNew
                id="entry_time"
                value={newEatMoment.given_entry || DateTime.now().toFormat("t")}
                setValue={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    entry_time: e || undefined,
                    given_entry: e || undefined
                  });
                }}
                greyBg
              />
              {/* <TimePicker
                value={newEatMoment.given_entry || DateTime.now().toFormat("t")}
                setValue={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    entry_time: e || undefined,
                    given_entry: e || undefined
                  });
                }}
                ampm={false}
                openTo="hours"
                label=""
                id="entry_time"
                sx={{
                  field: {
                    marginTop: 0
                  }
                }}
                // cancelLabel="Annuleer"
                greyBg
              /> */}
            </Grid>
            {!((eatMomentFavourites || []).length > 0 && !newEatMoment.is_favourite && !favLoaded && !newEatMoment.id) && <Grid xs={3} item></Grid>}

            <Grid item sm={6} xs={12} hidden={!checkFieldValidity('location')}>
              <TextInput
                id="location"
                helperIcon={checkHelperIcon('location')}
                placeholder={t("client.models.eat_moments.placeholders.location", "Thuis, in het park, ...")}
                caption={t("client.models.eat_moments.location", "Locatie")}
                autoComplete="location"
                autoFocus={false}
                backend
                greyBg
                value={newEatMoment.location || ''}
                onChange={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    location: e || undefined
                  });
                }}
              />
            </Grid>

            <Grid item sm={6} xs={12} hidden={!checkFieldValidity('participants')}>
              <TextInput
                id="participants"
                helperIcon={checkHelperIcon('participants')}
                placeholder={t("client.models.eat_moments.placeholders.participants", "Alleen, gezin, ...")}
                caption={t("client.models.eat_moments.participants", "Wie nam deel aan het beweegmoment")}
                autoComplete="participants"
                autoFocus={false}
                backend
                greyBg
                value={newEatMoment.participants || ''}
                onChange={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    participants: e || undefined
                  });
                }}
              />
            </Grid>

            <Grid item sm={6} xs={12} hidden={!checkFieldValidity('activity')}>
              <TextInput
                id="activity"
                helperIcon={checkHelperIcon('activity')}
                placeholder={t("client.models.eat_moments.placeholders.activity", "Wandelen, fietsen, lopen, ...")}
                caption={t("client.models.eat_moments.activity", "Activiteit tijdens het beweegmoment")}
                autoComplete="activity"
                autoFocus={false}
                backend
                greyBg
                value={newEatMoment.activity || ''}
                onChange={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    activity: e || undefined
                  });
                }}
              />
            </Grid>

            <Grid item sm={6} xs={12} hidden={!checkFieldValidity('hunger')}>
              <Typography variant={"caption"} sx={{marginTop: 1}}>{t("client.models.eat_moments.hunger", "Hongergevoel")}</Typography>
              {!!checkHelperIcon('hunger') && <Tooltip title={checkHelperIcon('hunger')}><HelpIcon sx={{
                float: 'right',
                color: theme.palette.primary.main
              }} /></Tooltip>}
              <br />
              <Slider
                value={newEatMoment.hunger || -1}
                onChange={(ev, val, e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    hunger: typeof(val) === "number" ? val : val[0] || undefined
                  });
                }}
                step={1}
                max={10}
                sx={{marginLeft: '10%', marginTop: 0, width: '80%'}}
                marks={[
                  {
                    value: 0,
                    label: t("client.models.eat_moments.hunger_none", "Geen"),
                  },
                  {
                    value: 10,
                    label: t("client.models.eat_moments.hunger_much", "Veel"),
                  },
                ]}
                valueLabelDisplay="on"
              />
            </Grid>

            <Grid item sm={6} xs={12} hidden={!checkFieldValidity('complaints')}>
              <TextInput
                id="complaints"
                helperIcon={checkHelperIcon('complaints')}
                placeholder={t("client.models.eat_moments.placeholders.complaints", "Krampen, geen adem, ...")}
                caption={t("client.models.eat_moments.complaints", "Klachten omtrent het beweegmoment")}
                autoComplete="complaints"
                autoFocus={false}
                backend
                greyBg
                value={newEatMoment.complaints || ''}
                onChange={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    complaints: e || undefined
                  });
                }}
              />
            </Grid>

            <Grid item sm={6} xs={12} hidden={!checkFieldValidity('feeling')}>
              <Typography variant={"caption"} sx={{marginTop: 1}}>{t("client.models.eat_moments.feeling", "Gevoel na het beweegmoment")}</Typography>
              {!!checkHelperIcon('hunger') && <Tooltip title={checkHelperIcon('feeling')}><HelpIcon sx={{
                float: 'right',
                color: theme.palette.primary.main
              }} /></Tooltip>}
              <br />

              <FaceVeryUnhappy
                activeValue={parseInt(newEatMoment.feeling?.toString() || '-1', 10)}
                onClick={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    feeling: e
                  });
                }}
              />
              <FaceUnhappy
                activeValue={newEatMoment.feeling || -1}
                onClick={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    feeling: e || undefined
                  });
                }}
              />
              <FaceHappy
                activeValue={newEatMoment.feeling || -1}
                onClick={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    feeling: e || undefined
                  });
                }}
              />
              <FaceVeryHappy
                activeValue={newEatMoment.feeling || -1}
                onClick={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    feeling: e || undefined
                  });
                }}
              />
            </Grid>

            <Grid item sm={6} xs={12} hidden={!checkFieldValidity('comment')}>
              <TextInput
                id="comment"
                helperIcon={checkHelperIcon('comment')}
                placeholder={t("client.models.eat_moments.placeholders.comment", "")}
                caption={t("client.models.eat_moments.comment", "Extra opmerkingen over het beweegmoment")}
                autoComplete="comment"
                autoFocus={false}
                multiline
                backend
                greyBg
                value={newEatMoment.comment || ''}
                onChange={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    comment: e || undefined
                  });
                }}
              />
            </Grid>

            {((!newEatMoment?.item && !newEatMoment?.get_item) || !newEatMoment?.meal_name) && <Grid item xs={12} sx={{marginBottom: -2}}>
              <Alert severity="warning">{t("general.eat_moment.no_forgetti_meal_name")}</Alert>
            </Grid>}

            <Grid item xs={12}>
              <Button
                id="save_em_btn"
                contained
                onClick={onClick}
                label={t("client.general.actions.eat_moment_save")}
              />
              <Button
                id="to_food"
                
                onClick={() => {
                  setCurrentTab("eat_diary.tab.food_detail");
                  // setCurrentTab("eat_diary.tab.food_lines");
                }}
                label={t("client.general.actions.go_to_food_detail")}
                sx={{
                  marginLeft: 2
                }}
              />
              {!!canDoCalculated && !favLoaded && <TextInput
                id="favourite_name"
                placeholder={t("client.models.eat_moments.placeholders.favourite_name")}
                noCaption
                backend
                greyBg
                value={newEatMoment.favourite_name || ''}
                onChange={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    favourite_name: e || undefined
                  });
                }}
                muiIcon={!!newEatMoment.is_favourite ? <Favorite color="primary" sx={{cursor: 'pointer'}} onClick={() => {
                  setNewEatMoment({
                    ...newEatMoment,
                    is_favourite: !newEatMoment.is_favourite
                  });
                }} /> : <FavoriteBorder color="primary" sx={{cursor: 'pointer'}} onClick={() => {
                  setNewEatMoment({
                    ...newEatMoment,
                    is_favourite: !newEatMoment.is_favourite
                  });
                }} />}
                sx={{
                  marginLeft: 1,
                  float: 'right',
                  width: '400px',
                  marginTop: 2
                }}
              />}
            </Grid>

          </Grid>
        </Box>}
        {currentTab === "eat_diary.tab.food_detail" && <Box>
          <Grid container sx={{
            marginTop: 1
          }} spacing={2}>
            <Grid item xs={!!newEatMoment?.get_item ? 4 : 12} sx={{
              ...(!newEatMoment?.get_item ? {
                display: 'none'
              } : {})
            }}>
              <Typography>{t("client.models.meal.current_item")}</Typography>
              <Avatar src={newEatMoment?.get_item} sx={{
                width: '100%',
                height: 'auto'
              }} variant="square" />
            </Grid>
            <Grid item xs={!!newEatMoment?.get_item ? 8 : 12}>
              <Grid container sx={{
                marginTop: 1
              }} spacing={2}>
                <Grid item xs={12} md={4}>
                  <FileUpload
                    caption={t("client.models.meal.item", "Afbeelding")}
                    fileInputName='eatmoment_meal_item'
                    food
                    selected={newEatMoment.item}
                    fallback={newEatMoment?.get_item}
                    onChange={(f) => {
                      setNewEatMoment({
                        ...newEatMoment,
                        item: f as File,
                        meal_name: !!f ? (newEatMoment.meal_name || "zie foto") : (newEatMoment.meal_name || '')
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextInput
                    id="meal_name"
                    caption={t("client.models.meal.name")}
                    backend
                    greyBg
                    value={newEatMoment.meal_name || ''}
                    onChange={(e) => {
                      setNewEatMoment({
                        ...newEatMoment,
                        meal_name: e || ''
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextInput
                    id="meal_description"
                    caption={t("client.models.meal.description")}
                    backend
                    greyBg
                    multiline
                    value={newEatMoment.meal_description || ''}
                    onChange={(e) => {
                      setNewEatMoment({
                        ...newEatMoment,
                        meal_description: e || ''
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    id="save_em_btn"
                    contained
                    onClick={onClick}
                    label={t("client.general.actions.eat_moment_save")}
                  />
                  <Button
                    id="to_food"
                    
                    onClick={() => {
                      setCurrentTab("eat_diary.tab.food_lines");
                    }}
                    label={t("client.general.actions.go_to_food_lines")}
                    sx={{
                      marginLeft: 2
                    }}
                  />
                  {!!canDoCalculated && !favLoaded && <TextInput
                    id="favourite_name"
                    placeholder={t("client.models.eat_moments.placeholders.favourite_name")}
                    noCaption
                    backend
                    greyBg
                    value={newEatMoment.favourite_name || ''}
                    onChange={(e) => {
                      setNewEatMoment({
                        ...newEatMoment,
                        favourite_name: e || undefined
                      });
                    }}
                    muiIcon={!!newEatMoment.is_favourite ? <Favorite color="primary" sx={{cursor: 'pointer'}} onClick={() => {
                      setNewEatMoment({
                        ...newEatMoment,
                        is_favourite: !newEatMoment.is_favourite
                      });
                    }} /> : <FavoriteBorder color="primary" sx={{cursor: 'pointer'}} onClick={() => {
                      setNewEatMoment({
                        ...newEatMoment,
                        is_favourite: !newEatMoment.is_favourite
                      });
                    }} />}
                    sx={{
                      marginLeft: 1,
                      float: 'right',
                      width: '400px',
                      marginTop: 2
                    }}
                  />}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>}
        {currentTab === "eat_diary.tab.food_lines" && <Box>
          <Grid container sx={{
            marginTop: 1
          }} spacing={2}>
            {/* <Grid item xs={12}>
              <Typography variant="h5">{t("client.models.food_entries.title", "Gegevens over de voeding")}</Typography>
              {!!checkHelperIcon('hunger') && <Tooltip title={checkHelperIcon('food_entries')}><HelpIcon sx={{
                float: 'right',
                color: theme.palette.primary.main,
                marginTop: -3
              }} /></Tooltip>}
            </Grid> */}

            <Grid container spacing={1} item xs={12}>
              {!!addingFoodLine && <Grid container spacing={1} item xs={12}>
                {!!addingFoodLinePerGram && <>
                  <Grid item xs={5}>
                    <TextInput
                      id="search_food"
                      placeholder={t("client.models.calculated_food_entries.placeholders.search_foodstuff")}
                      caption={t("client.models.calculated_food_entries.search_foodstuff")}
                      backend
                      greyBg
                      value={searchFood || ''}
                      onChange={(e) => {
                        setSearchFood(e);
                      }}
                    />
                    <Typography sx={{fontSize: '0.7rem', fontStyle: 'italic', marginBottom: 1}}>{(foodstuffs || []).length} {t("general.results_found")}</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Select
                      fullWidth
                      caption={t("coach.models.calculated_food_entries.foodstuff")}
                      starred
                      displayEmpty
                      id="foodstuff"
                      value={newFoodEntry.foodstuff_id || ''}
                      setValue={(v) => {
                        setNewFoodEntry({
                          ...newFoodEntry,
                          foodstuff_id: !!v ? parseInt(v.toString(), 10) : undefined,
                          foodstuff: (([...(foodstuffs || []), ...(favFoodstuffs || [])]).filter(fff => fff.id === parseInt(v.toString(), 10))[0] || {})
                        });
                      }}
                    >
                      <MenuItem value="">------</MenuItem>
                      {(favFoodstuffs || []).map(fs => <MenuItem
                        // disabled={!!client.consent_status?.is_blocked}
                        value={fs.id}
                        // sx={{
                        //   ...(!!client.consent_status?.is_blocked ? {
                        //     color: 'grey',
                        //     textDecoration: 'line-through'
                        //   } : {})
                        // }}
                      >{selectContentWithId(fs.full_name || {})}</MenuItem>)}
                      {(favFoodstuffs || []).length > 0 && <Divider />}
                      {(foodstuffs || []).map(fs => <MenuItem
                        // disabled={!!client.consent_status?.is_blocked}
                        value={fs.id}
                        // sx={{
                        //   ...(!!client.consent_status?.is_blocked ? {
                        //     color: 'grey',
                        //     textDecoration: 'line-through'
                        //   } : {})
                        // }}
                      >{selectContentWithId(fs.full_name || {})}</MenuItem>)}
                    </Select>
                    {!!newFoodEntry?.foodstuff_id && (favFoodstuffs || []).filter(fff => fff.id === newFoodEntry?.foodstuff_id).length < 1 && <Button
                      id="add_fav"
                      text
                      size='small'
                      onClick={() => {
                        mutationCustomAction.mutate({
                          formData: {
                            object: 'foodstuff',
                            class_action: 'custom_api_add_fav',
                            id: newFoodEntry?.foodstuff_id
                          }
                        });
                      }}
                      label={t("client.general.actions.add_as_favourite")}
                      startIcon={<FavoriteBorder />}
                      sx={{
                        // float: 'right'
                        marginTop: 0
                      }}
                    />}
                    {!!newFoodEntry?.foodstuff_id && (favFoodstuffs || []).filter(fff => fff.id === newFoodEntry?.foodstuff_id).length > 0 && <Button
                      id="remove_fav"
                      text
                      size='small'
                      onClick={() => {
                        mutationCustomAction.mutate({
                          formData: {
                            object: 'foodstuff',
                            class_action: 'custom_api_add_fav',
                            id: newFoodEntry?.foodstuff_id,
                            remove: 1
                          }
                        });
                      }}
                      label={t("client.general.actions.remove_as_favourite")}
                      startIcon={<Favorite />}
                      sx={{
                        // float: 'right'
                        marginTop: 0
                      }}
                    />}
                  </Grid>
                  <Grid item xs={8}>
                    <Select
                      fullWidth
                      caption={t("coach.models.calculated_food_entries.foodstuff_measure")}
                      starred
                      displayEmpty
                      id="foodstuff"
                      value={newFoodEntry.foodstuff_measure_id || ''}
                      setValue={(v) => {
                        setNewFoodEntry({
                          ...newFoodEntry,
                          foodstuff_measure_id: !!v ? parseInt(v.toString(), 10) : undefined,
                          foodstuff_measure: (((foodstuffs || []).filter(fff => fff.id === parseInt(v.toString(), 10))[0] || {}).foodstuff_measures || [])[0]
                        });
                      }}
                    >
                      <MenuItem value="">{t("coach.models.calculated_food_entries.input_grams")}</MenuItem>
                      {(newFoodEntry?.foodstuff?.foodstuff_measures || []).map(fs => <MenuItem
                        // disabled={!!client.consent_status?.is_blocked}
                        value={fs.id}
                        // sx={{
                        //   ...(!!client.consent_status?.is_blocked ? {
                        //     color: 'grey',
                        //     textDecoration: 'line-through'
                        //   } : {})
                        // }}
                      >{selectContentWithId(fs.full_name || {})} ({fs.grams}gr)</MenuItem>)}
                    </Select>
                  </Grid>
                  <Grid item xs={4}>
                    <TextInput
                      id="portion"
                      caption={t("client.models.calculated_food_entries.portion")}
                      backend
                      greyBg
                      value={foodPortion || (!!newFoodEntry.foodstuff_portion ? parseFloat(parseFloat(newFoodEntry.foodstuff_portion?.toString() || '')?.toFixed(2))?.toString() : '') || ''}
                      onChange={(e) => {
                        setFoodPortion(e);
                        // setNewFoodEntry({
                        //   ...newFoodEntry,
                        //   foodstuff_portion: !!e ? parseFloat(e.toString().replace(",", ".")) : undefined
                        // });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      id="add_fe_btn"
                      contained
                      disabled={!newFoodEntry.foodstuff_portion || !newFoodEntry.foodstuff_id}
                      onClick={() => {
                        if (typeof(editing) !== 'undefined') {
                          let newFoodEntries = [...(newEatMoment.food_entries || [])];
                          newFoodEntries[editing] = newFoodEntry;
                          setNewEatMoment({
                            ...newEatMoment,
                            food_entries: newFoodEntries
                          });
                        } else {
                          setNewEatMoment({
                            ...newEatMoment,
                            food_entries: [
                              ...(newEatMoment.food_entries || []),
                              newFoodEntry
                            ]
                          });
                        }
                        setNewFoodEntry({entry: '', item: undefined, foodstuff_id: undefined, foodstuff_portion: undefined, foodstuff_measure_id: undefined});
                        setSearchFood('');
                        setFoodPortion('');
                        setAddingFoodLine(false);
                        setEditing(undefined);
                      }}
                      label={t((typeof(editing) !== 'undefined') ? "client.general.actions.food_entry_edit" : "client.general.actions.food_entry_add")}
                    />
                    <Button
                      id="empty_fe_btn"
                      text
                      onClick={() => {
                        setNewFoodEntry({entry: '', item: undefined, foodstuff_id: undefined, foodstuff_portion: undefined, foodstuff_measure_id: undefined});
                        setSearchFood('');
                        setFoodPortion('');
                        setEditing(undefined);
                        setAddingFoodLine(false);
                      }}
                      label={t("client.general.actions.cancel")}
                      sx={{
                        // float: 'right'
                        marginLeft: 1
                      }}
                    />
                  </Grid>
                </>}
                {!addingFoodLinePerGram && <>
                  <Grid item xs={12}>
                    <TextInput
                      id="entry_entry"
                      placeholder={t("client.models.food_entries.placeholders.entry", "1 boterham met kaas")}
                      caption={t("client.models.food_entries.entry", "Voedingsmiddel")}
                      backend
                      greyBg
                      value={newFoodEntry.entry || ''}
                      onChange={(e) => {
                        setNewFoodEntry({
                          ...newFoodEntry,
                          entry: e || ''
                        });
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      id="entry_comment"
                      placeholder={t("client.models.food_entries.placeholders.comment", "")}
                      caption={t("client.models.food_entries.comment", "Opmerkingen")}
                      backend
                      multiline
                      greyBg
                      value={newFoodEntry.comment || ''}
                      onChange={(e) => {
                        setNewFoodEntry({
                          ...newFoodEntry,
                          comment: e || ''
                        });
                      }}
                    />
                  </Grid>
                  {/* <Grid item xs={12}>
                    <FileUpload
                      caption={t("client.models.food_entries.item", "Afbeelding")}
                      fileInputName='eatmoment_foodentry_item'
                      food
                      selected={newFoodEntry.item}
                      onChange={(f) => {
                        setNewFoodEntry({
                          ...newFoodEntry,
                          item: f as File
                        });
                      }}
                    />
                  </Grid> */}
                  <Grid item xs={12}>
                    <Button
                      id="add_fe_btn"
                      contained
                      disabled={!newFoodEntry.entry}
                      onClick={() => {
                        if (!!newFoodEntry.entry) {
                          if (typeof(editing) !== 'undefined') {
                            let newFoodEntries = [...(newEatMoment.food_entries || [])];
                            newFoodEntries[editing] = newFoodEntry;
                            setNewEatMoment({
                              ...newEatMoment,
                              food_entries: newFoodEntries
                            });
                          } else {
                            setNewEatMoment({
                              ...newEatMoment,
                              food_entries: [
                                ...(newEatMoment.food_entries || []),
                                newFoodEntry
                              ]
                            });
                          }
                          setNewFoodEntry({entry: '', item: undefined, foodstuff_portion: undefined});
                          setEditing(undefined);
                          setAddingFoodLine(false);
                        }
                      }}
                      label={t((typeof(editing) !== 'undefined') ? "client.general.actions.food_entry_edit" : "client.general.actions.food_entry_add")}
                    />
                    <Button
                      id="empty_fe_btn"
                      text
                      onClick={() => {
                        setNewFoodEntry({entry: '', item: undefined, foodstuff_portion: undefined});
                        setEditing(undefined);
                        setAddingFoodLine(false);
                      }}
                      label={t("client.general.actions.cancel")}
                      sx={{
                        // float: 'right'
                        marginLeft: 1
                      }}
                    />
                  </Grid>
                </>}
              </Grid>}
              {!addingFoodLine && <Grid item xs={12} md={12}>
                <Button
                  id="add_open_food"
                  startIcon={<Add />}
                  onClick={() => {
                    setAddingFoodLinePerGram(false);
                    setAddingFoodLine(true);
                  }}
                  label={t("client.general.actions.add_food_entry_open")}
                  sx={{
                    // float: 'right'
                    marginTop: 0,
                    marginRight: 1
                  }}
                />
                {!!canDoCalculated && <Button
                  id="add_calc_food"
                  startIcon={<Add />}
                  onClick={() => {
                    setAddingFoodLinePerGram(true);
                    setAddingFoodLine(true);
                  }}
                  label={t("client.general.actions.add_food_entry_calculated")}
                  sx={{
                    // float: 'right'
                    marginTop: 0,
                    marginRight: 1
                  }}
                />}
                <Divider sx={{marginTop: 1}} />
                {(newEatMoment.food_entries || []).length < 1 && <Typography sx={{fontStyle: 'italic', textAlign: 'center'}}>{t("client.models.eat_moments.food_entries_error", "Er is minstens 1 voedingslijn nodig")}</Typography>}
                {(newEatMoment.food_entries || []).length > 0 && <Box sx={{
                  maxHeight: '380px',
                  overflow: 'auto'
                }}><List>
                  {(newEatMoment.food_entries || []).map((entry, i) => {
                    return <ListItem key={entry.id ?? i}>
                    <ListItemIcon>
                      <FoodEntryAvatar entry={entry} />
                    </ListItemIcon>
                    {!!entry.entry && <ListItemText primary={entry.entry} secondary={entry.comment} />}
                    {!!entry.foodstuff_id && <ListItemText
                      primary={selectContentWithId(entry.foodstuff?.full_name || {})}
                      secondary={`${parseFloat(parseFloat(entry.foodstuff_portion?.toString() || '')?.toFixed(2) || '')} ${!!entry.foodstuff_measure_id ? selectContentWithId(((entry?.foodstuff?.foodstuff_measures || []).filter(fff => fff.id === entry.foodstuff_measure_id)[0] || {}).full_name || {}) : ''}${!entry.foodstuff_measure_id ? 'gr' : ''}`}
                    />}
                    <ListItemSecondaryAction>
                      <Tooltip title={"Edit"}>
                        <IconButton onClick={(e) => {
                          e.preventDefault();
                          // @ts-ignore
                          e.currentTarget.focus();
                          setNewFoodEntry(entry);
                          setSearchFood(selectContentWithId(entry.foodstuff?.full_name || {}));
                          setEditing(i);
                          setAddingFoodLinePerGram(!entry.entry);
                          setAddingFoodLine(true);
                        }}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={"Delete"}>
                        <IconButton onClick={(e) => {
                          e.preventDefault();
                          // @ts-ignore
                          e.currentTarget.focus();

                          let newFoodEntries = [...(newEatMoment.food_entries || [])];
                          newFoodEntries.splice(i, 1);
                          setNewEatMoment({
                            ...newEatMoment,
                            food_entries: newFoodEntries
                          });
                        }}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>;
                  })}
                </List></Box>}
              </Grid>}
            </Grid>

            {!addingFoodLine && <Grid item xs={12}>
              <Button
                id="save_em_btn"
                contained
                onClick={onClick}
                label={t("client.general.actions.eat_moment_save")}
              />
              {!!canDoCalculated && !favLoaded && <TextInput
                id="favourite_name"
                placeholder={t("client.models.eat_moments.placeholders.favourite_name")}
                noCaption
                backend
                greyBg
                value={newEatMoment.favourite_name || ''}
                onChange={(e) => {
                  setNewEatMoment({
                    ...newEatMoment,
                    favourite_name: e || undefined
                  });
                }}
                muiIcon={!!newEatMoment.is_favourite ? <Favorite color="primary" sx={{cursor: 'pointer'}} onClick={() => {
                  setNewEatMoment({
                    ...newEatMoment,
                    is_favourite: !newEatMoment.is_favourite
                  });
                }} /> : <FavoriteBorder color="primary" sx={{cursor: 'pointer'}} onClick={() => {
                  setNewEatMoment({
                    ...newEatMoment,
                    is_favourite: !newEatMoment.is_favourite
                  });
                }} />}
                sx={{
                  marginLeft: 1,
                  float: 'right',
                  width: '400px',
                  marginTop: 2
                }}
              />}
            </Grid>}
          </Grid>
        </Box>}
      </>}
      actions={<DialogActions sx={{borderTop: '1px solid transparent', borderColor: theme.palette.primary.main}}>
        {!!newEatMoment?.id && <Btn
          onClick={() => {
            setRemovalOpen(true);
          }}
          color="secondary"
          sx={{marginRight: 'auto'}}
        >{t("client.eat_moment.actions.destroy")}</Btn>}
        {Object.entries(originalEatMmnt || {}).filter(fff => !!fff[1]).sort().toString()!==Object.entries(newEatMoment).filter(fff => !!fff[1]).sort().toString() && <Typography sx={{
          fontStyle: 'italic',
          color: theme.palette.secondary.main,
          fontSize: '0.8rem',
          marginRight: 1
        }}>{t("general.notice.unsaved_changes")}</Typography>}
        <Btn
          onClick={(e) => {
            setNewEatMoment({feeling: -1, hunger: -1});
            setSearchFood('');
            setNewFoodEntry({entry: '', item: undefined, foodstuff_id: undefined, foodstuff_portion: undefined, foodstuff_measure_id: undefined});
            setAddingFoodLine(false);
            setCurrentTab((Object.values(login?.full_eat_groups || {}).filter(fff => fff.practices.length > 0).length > 0 ? 'eat_diary.groups.short' : 'eat_diary.groups.everything'));
            setOpen(false);
          }}
          color="primary"
        >{t("coach.general.actions.close")}</Btn>
        <Btn
          onClick={onClick}
          color="primary"
        >{t("coach.general.actions.save")}</Btn>
      </DialogActions>}
      open={open}
      setOpen={setOpen}
    />

    <Removal
      open={removalOpen}
      setOpen={setRemovalOpen}
      execute={(e) => {
        if (!!onDelete) onDelete();
        setOpen(false);
      }}
    />
  </>);
}
